/*    
<summary>
   This component manages reservation settings & performs CRUD operations.
   CRUD (Create, Read, Update, Delete)
   Developer: Aashish Singh, Created Date: 25-Aug-2022, 
   Last Updated By: Mohammad Saquib Khan, Updated Date: 05-Dec-2022
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import "./reservation-settings.css";
import { useStore } from "../../../contexts/store-provider";
import { formatMessage } from "../../../translations/format-message";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import AddUpdateReservationSettings from "./components/add-update-reservation-settings";
import { useHistory, useLocation, useParams } from "react-router-dom";
import RoutesConstants from "../../../constants/routes.constant";
import DropdownInput from "../../../shared-components/input/DropdownInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import { getStoreIdFromLocalStorage, getUserTypeFromLocalStorage } from "../../../helpers/localstorage-helper";
import userTypeEnum from "../../../constants/enums/user-type-enum";

const ReservationSettings: React.FC = (props: any) => {
  const { masterManagementStore } = useStore();
  const {
    inProgress,
    GetReservationSettingByIdService,
    addUpdateReservationSettingState,
    getByIdReservationSettingState,
    GetAllStoreOptionService,
    GetAllTimeService,
    getAllStoreOptions,
    setDisableAction
  } = masterManagementStore;

  const location = useLocation();
  const history = useHistory();
  const fromNavbar = (location.state as { fromNavbar?: boolean })?.fromNavbar ?? false;
  
  const [selectedId, setSelectedId] = useState<number>(-1);
  let storeIdNumber = (location.state as { storeId?: number })?.storeId ?? 0;
  
  
  const [stores, setStores] = useState<{ id: number; name: string }[]>([]);
  const userType = getUserTypeFromLocalStorage();
  if(userType === userTypeEnum[userTypeEnum.Store]){
    storeIdNumber = getStoreIdFromLocalStorage() || 0;
  }
  const [storeIdNo, setStoreIdNo] = useState<number>(storeIdNumber);


  const handleStoreChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newStoreId = Number(event.target.value);
    setStoreIdNo(newStoreId);

    masterManagementStore.resetGetByIdReservationSettingState();

    if (newStoreId > 0) {
      history.replace({
        pathname: RoutesConstants.ReservationSettings, 
        state: { storeId: newStoreId, fromNavbar }, 
      });
      await callGetReservationSettingsService(newStoreId);
    } else {
      setStoreIdNo(0);
      history.replace({
        pathname: RoutesConstants.ReservationSettings,
        state: { storeId: newStoreId, fromNavbar },
      });
      
      await callGetReservationSettingsService(0);
    }
  };


  useEffect(() => {
    GetAllTimeService();
    GetAllStoreOptionService();
    return(()=>{
      setDisableAction(false);
    })
  }, []);
  /**
   * This function should call the API to fetch reservation settings.
   */
  const callGetReservationSettingsService = async (storeId?: number) => {

    masterManagementStore.resetGetByIdReservationSettingState();
    const idToFetch = storeId ?? storeIdNumber;

    if (idToFetch > 0) {
      const data = await GetReservationSettingByIdService(idToFetch);
      if (data && data.Id) {
        setSelectedId(data.Id);
      } else {
        setSelectedId(-1);
      }
    } else {
      const defaultData = await GetReservationSettingByIdService(0);
      if (defaultData && defaultData.Id) {
        setSelectedId(defaultData.Id);
      } else {
        setSelectedId(-1);
      }
    }
  };

  useEffect(() => {
    if (storeIdNumber === 0) {
      setStoreIdNo(-1);
    }
    masterManagementStore.resetGetByIdReservationSettingState();
    callGetReservationSettingsService(storeIdNumber);
  }, [storeIdNumber]);

  /**
   * Handles manual refresh.
   */
  const refreshClickHandler = () => {
    callGetReservationSettingsService();
  };

  return (
    <React.Fragment>
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="reservation_settings" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="various_settings" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="reservation_settings" />
                </li>
              </ol>
            </div>
          </div>
          
          <div className="col-6">
            <ul className="add-list">
              <li title={formatMessage("refresh")}>
                <button className="btn btn-light" onClick={refreshClickHandler}>
                  <svg
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-arrow-repeat m-0"
                    viewBox="0 0 16 16"
                  >
                    <path d="M11.534 7h3.932a.25.25 0 0 1 .192.41l-1.966 2.36a.25.25 0 0 1-.384 0l-1.966-2.36a.25.25 0 0 1 .192-.41zm-11 2h3.932a.25.25 0 0 0 .192-.41L2.692 6.23a.25.25 0 0 0-.384 0L.342 8.59A.25.25 0 0 0 .534 9z" />
                    <path
                      fillRule="evenodd"
                      d="M8 3c-1.552 0-2.94.707-3.857 1.818a.5.5 0 1 1-.771-.636A6.002 6.002 0 0 1 13.917 7H12.9A5.002 5.002 0 0 0 8 3zM3.1 9a5.002 5.002 0 0 0 8.757 2.182.5.5 0 1 1 .771.636A6.002 6.002 0 0 1 2.083 9H3.1z"
                    />
                  </svg>
                </button>
              </li>
             
              <li>
                <LanguageSlider />
              </li>
              <li>
                {/* Right Side - Dropdown */}
                {
                  userType !== userTypeEnum[userTypeEnum.Store] && (
                    <>
                      {fromNavbar && (
                      <div className="input-group search-dropdown">
                        <div className="input-group-text">
                          <span role="button">
                            <FontAwesomeIcon icon={faSearch} />
                          </span>
                        </div>

                        <DropdownInput
                          className="form-control"
                          value={storeIdNo}
                          onChange={(selected) => handleStoreChange(selected)}
                          options={getAllStoreOptions.map((store) => ({
                            label: store.value,
                            value: store.id,
                          }))}
                        />
                      </div>
                  )}
                    </>
                  )
                }
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Show loader when API calls are in progress */}
      <FormLoader
        loading={
          inProgress || getByIdReservationSettingState.inProgress || addUpdateReservationSettingState.inProgress
        }
      />
        <div className="card mt-3">
            <div className="row align-items-center justify-content-between px-3 py-2">

              <div className="col-auto">
                <h3 className="mb-0">
                  {
                    userType !== userTypeEnum[userTypeEnum.Store] && 
                    <>
                      {
                        storeIdNo <= 0 &&
                        <FormattedMessage id="please_select_store" />
                      }
                    </>
                  }
                </h3>
              </div>

              

            </div>
            <div className="table-responsive">
              {storeIdNo > 0 && (
                <AddUpdateReservationSettings
                  id={selectedId}
                  onSuccess={callGetReservationSettingsService}
                />
              )}
            </div>
        </div>
    </React.Fragment>
  );
};

export default observer(ReservationSettings);
